import React from 'react'
import styled from 'styled-components'
import DocumentTitle from 'react-document-title'
import JSZip from 'jszip'
import {
    isAuthConfigured,
    getSession,
    signIn,
    signOut,
} from '../../lib/supabaseAuth'
import { getMyTournaments, uploadMatch } from '../../lib/supabaseUpload'

const Page = styled.div`
    max-width: 560px;
    margin: 0 auto;
    padding: 2rem 0;
`

const Box = styled.div`
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 28px;
`

const Title = styled.h1`
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
`

const Subtitle = styled.p`
    color: var(--grey-1);
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
`

const Input = styled.input`
    width: 100%;
    padding: 10px 14px;
    margin-bottom: 14px;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--grey-2);
    font-size: 1rem;
`

const Select = styled.select`
    width: 100%;
    padding: 10px 14px;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--grey-2);
    font-size: 1rem;
    cursor: pointer;
`

const Button = styled.button`
    padding: 10px 20px;
    background: var(--accent);
    color: var(--bg);
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    &:hover { background: var(--accent-hover); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const Error = styled.div` color: #ef4444; font-size: 0.9rem; margin-top: 8px; `
const Success = styled.div` color: #22c55e; font-size: 0.9rem; margin-top: 8px; `
const LogoutRow = styled.div`
    margin-bottom: 24px;
    font-size: 0.9rem;
    color: var(--grey-1);
`
const LogoutLink = styled.span`
    color: var(--accent);
    cursor: pointer;
    &:hover { text-decoration: underline; }
`
const Label = styled.label` display: block; margin-bottom: 8px; font-weight: 500; `
const Section = styled.div` margin-bottom: 28px; `

const MAP_OPTIONS = [
    'Erangel',
    'Miramar',
    'Sanhok',
    'Vikendi',
    'Rondo',
]

class Admin extends React.Component {
    state = {
        session: getSession(),
        email: '',
        password: '',
        loginError: '',
        loginLoading: false,
        tournaments: [],
        tournamentsLoading: true,
        selectedTournamentId: '',
        matchTitle: '',
        mapName: '',
        uploadLoading: false,
        uploadError: '',
        uploadSuccess: false,
    }

    refreshSession = () => {
        this.setState({ session: getSession() })
    }

    handleLoginSubmit = async e => {
        e.preventDefault()
        this.setState({ loginError: '', loginLoading: true })
        const { email, password } = this.state
        const res = await signIn(email, password)
        this.setState({ loginLoading: false })
        if (res.error) {
            this.setState({ loginError: res.error.message || 'Bir hata oluştu' })
            return
        }
        this.setState({ session: getSession() })
    }

    handleLogout = () => {
        signOut()
        this.setState({ session: null })
    }

    loadTournaments = async () => {
        const s = getSession()
        if (!s) return
        const list = await getMyTournaments(s.access_token, s.user.id)
        this.setState({
            tournaments: list || [],
            tournamentsLoading: false,
            selectedTournamentId: (list && list[0] && list[0].id) || '',
        })
    }

    componentDidMount() {
        if (this.state.session) this.loadTournaments()
    }

    componentDidUpdate(prevProps, prevState) {
        if (!prevState.session && this.state.session) this.loadTournaments()
    }

    readReplayFile = file => {
        return new Promise((resolve, reject) => {
            const isZip = file.name.toLowerCase().endsWith('.zip')
            const reader = new FileReader()
            reader.onload = async e => {
                try {
                    if (isZip) {
                        const zip = await JSZip.loadAsync(e.target.result)
                        const replayFile = zip.file('replay.json')
                        if (!replayFile) return reject(new Error('ZIP içinde replay.json yok'))
                        const text = await replayFile.async('string')
                        resolve(JSON.parse(text))
                    } else {
                        resolve(JSON.parse(e.target.result))
                    }
                } catch (err) {
                    reject(err)
                }
            }
            reader.onerror = () => reject(new Error('Dosya okunamadı'))
            if (isZip) reader.readAsArrayBuffer(file)
            else reader.readAsText(file)
        })
    }

    handleUpload = async e => {
        const input = e.target
        const file = input && input.files && input.files[0]
        if (!file) return
        const tournamentId = this.state.selectedTournamentId
        if (!tournamentId) {
            this.setState({ uploadError: 'Önce yetkili olduğunuz bir turnuva seçin.' })
            return
        }
        const { matchTitle, mapName } = this.state
        if (!mapName) {
            this.setState({ uploadError: 'Lütfen harita seçin.' })
            if (input) input.value = ''
            return
        }
        const s = getSession()
        if (!s) return
        this.setState({ uploadLoading: true, uploadError: '', uploadSuccess: false })
        try {
            const replay = await this.readReplayFile(file)
            if (!replay || replay.format !== 'pob' || replay.version !== 2) {
                this.setState({ uploadError: 'Geçerli POB v2 replay dosyası gerekli.', uploadLoading: false })
                return
            }
            const { error } = await uploadMatch(s.access_token, tournamentId, {
                title: (matchTitle && matchTitle.trim()) || 'Maç',
                map_name: mapName,
                replay_json: replay,
            })
            if (error) {
                this.setState({ uploadError: error, uploadLoading: false })
                return
            }
            this.setState({ uploadSuccess: true, uploadLoading: false })
        } catch (err) {
            this.setState({
                uploadError: err.message || 'Yükleme başarısız',
                uploadLoading: false,
            })
        }
        if (input) input.value = ''
    }

    render() {
        if (!isAuthConfigured()) {
            return (
                <Page>
                    <DocumentTitle title="Admin | DropScore" />
                    <Box>
                        <Title>Admin</Title>
                        <Subtitle>
                            Sunucu ayarlı değil. Yapılandırma gerekli.
                        </Subtitle>
                    </Box>
                </Page>
            )
        }

        if (!this.state.session) {
            const { email, password, loginError, loginLoading } = this.state
            return (
                <Page>
                    <DocumentTitle title="Admin Giriş | DropScore" />
                    <Box>
                        <Title>Admin Giriş</Title>
                        <Subtitle>Replay yüklemek için üye girişi yapın. Yeni hesap yönetici tarafından oluşturulur.</Subtitle>
                        <form onSubmit={this.handleLoginSubmit}>
                            <Input
                                type="email"
                                placeholder="E-posta"
                                value={email}
                                onChange={e => this.setState({ email: e.target.value })}
                                required
                            />
                            <Input
                                type="password"
                                placeholder="Şifre"
                                value={password}
                                onChange={e => this.setState({ password: e.target.value })}
                                required
                            />
                            {loginError && <Error>{loginError}</Error>}
                            <Button type="submit" disabled={loginLoading} style={{ width: '100%', marginTop: 8 }}>
                                {loginLoading ? '...' : 'Giriş yap'}
                            </Button>
                        </form>
                    </Box>
                </Page>
            )
        }

        const {
            tournaments,
            tournamentsLoading,
            selectedTournamentId,
            matchTitle,
            mapName,
            uploadLoading,
            uploadError,
            uploadSuccess,
        } = this.state
        const session = this.state.session

        return (
            <Page>
                <DocumentTitle title="Admin — Replay Yükle | DropScore" />
                <LogoutRow>
                    <span>{session.user.email}</span>
                    {' · '}
                    <LogoutLink onClick={this.handleLogout}>Çıkış</LogoutLink>
                </LogoutRow>
                <Title>Admin — Replay Yükle</Title>
                <Subtitle>
                    Yetkili olduğunuz turnuvayı seçin; sadece üye atandığınız turnuvalara maç yükleyebilirsiniz. Turnuvalar Supabase üzerinden manuel oluşturulur.
                </Subtitle>

                <Section>
                    <Label>Turnuva</Label>
                    {tournamentsLoading ? (
                        <Subtitle>Yükleniyor...</Subtitle>
                    ) : (tournaments || []).length === 0 ? (
                        <Subtitle>Size atanmış turnuva bulunamadı. Supabase’te turnuva oluşturup tournament_members ile kullanıcınızı ekleyin.</Subtitle>
                    ) : (
                        <Select
                            value={selectedTournamentId}
                            onChange={e => this.setState({ selectedTournamentId: e.target.value })}
                        >
                            <option value="">— Seçin —</option>
                            {(tournaments || []).map(t => (
                                <option key={t.id} value={t.id}>
                                    {t.name || t.title || t.id}
                                </option>
                            ))}
                        </Select>
                    )}
                </Section>

                <Section>
                    <Label>Harita</Label>
                    <Select
                        value={mapName}
                        onChange={e => this.setState({ mapName: e.target.value })}
                    >
                        <option value="">— Harita seçin —</option>
                        {MAP_OPTIONS.map(m => (
                            <option key={m} value={m}>{m}</option>
                        ))}
                    </Select>
                </Section>

                <Section>
                    <Label>Başlık (isteğe bağlı, örn. Çeyrek final)</Label>
                    <Input
                        type="text"
                        placeholder="Örn. Çeyrek final, Yarı final, Final"
                        value={matchTitle}
                        onChange={e => this.setState({ matchTitle: e.target.value })}
                    />
                </Section>

                <Section>
                    <Label>Replay dosyası (ZIP veya JSON)</Label>
                    <input
                        type="file"
                        accept=".zip,.json"
                        onChange={this.handleUpload}
                        disabled={!selectedTournamentId || uploadLoading}
                        style={{ marginTop: 8 }}
                    />
                    {uploadError && <Error>{uploadError}</Error>}
                    {uploadSuccess && <Success>Yükleme başarılı.</Success>}
                    {uploadLoading && <Subtitle style={{ marginTop: 8 }}>Yükleniyor...</Subtitle>}
                </Section>
            </Page>
        )
    }
}

export default Admin



// WEBPACK FOOTER //
// ./src/routes/Admin/index.js